import { z } from 'zod'

export type MFAMethod = 'None' | 'SMS' | 'Email' | 'SoftwareToken' | 'Unknown'

export type MFAServerMethod = 'EMAIL' | 'SMS'

export type MFAAttributeNameMap = {
  None: 'none'
  SMS: 'phone_number'
  Email: 'email'
  SoftwareToken: 'software_token_mfa_code'
  Unknown: 'unknown'
}

export type MFAAttributeName = MFAAttributeNameMap[MFAMethod]

export type MfaAuthRequestData = {
  accountNumber: number
  password: string
  deviceSecret?: string
  deviceGroupKey?: string
  deviceKey?: string
}

export type MfaAuthenticateWithMfaRequestData = {
  accountNumber: number
  mfaSessionId: string
  mfaFactor: MFAServerMethod
  mfaCode: string
}

export type MfaVerificationRequestData = {
  attributeName: MFAAttributeName
}

export type MfaCompleteVerificationRequestData = MfaVerificationRequestData & {
  code: string
}

export type MfaUpdateAuthRequestData = {
  preferredFactor: MFAMethod
}

export type MfaStartAttributeVerificationResponseData = {
  attributeName: MFAAttributeName
  destination: string // the (censored?) email or phone number
}

export type CognitoUser = {
  accountNumber: string
  isEnabled: boolean
  userCreatedDate: string
  userLastModifiedDate: string
  userAttributes: {
    sub: string
    email: string | undefined
    email_verified: string
    phone_number: string | undefined
    phone_number_verified: string
    given_name: string
    family_name: string
  }
  preferredFactor: MFAMethod
}

export type MfaError = {
  status?: number
  error?: string
  message?: string
}

const deviceSchema = z.object({
  deviceName: z.string(),
  deviceStatus: z.string(),
  deviceKey: z.string(),
  deviceCreateDate: z.nullable(z.string()),
  deviceLastAuthenticatedDate: z.nullable(z.string()),
  deviceLastModifiedDate: z.nullable(z.string()),
})

export type Device = z.infer<typeof deviceSchema>

export const devicesResponseSchema = z.object({
  devices: z.array(deviceSchema),
})

export type DevicesResponse = z.infer<typeof devicesResponseSchema>

export const logonResponseSchema = z.object({
  accountNumber: z.number(),
  status: z.enum(['MFA_REQUIRED', 'PASSWORD_CHANGE_REQUIRED', 'SUCCESSFUL', 'FAILED']),
  error: z.nullable(z.string()),
  mfaSessionId: z.string(),
  mfaFactor: z.enum(['SMS', 'EMAIL']),
  mfaHint: z.string(),
  newDeviceKey: z.optional(z.string()),
  newDeviceGroupKey: z.optional(z.string()),
})

export type LogonResponse = z.infer<typeof logonResponseSchema>

export const mfaLogonResponseSchema = z.object({
  status: z.enum(['SUCCESSFUL', 'FAILED']),
  newDeviceKey: z.optional(z.string()),
  newDeviceGroupKey: z.optional(z.string()),
})

export type MfaLogonResponse = z.infer<typeof mfaLogonResponseSchema>
